// Which product the tenant is looking at is a route fact (#/p/<slug>/…), but
// System pages (Integrations, Team, Billing) carry no slug, and two things
// there leave the app entirely: the Google connect redirect and the
// location.reload() after saving database credentials. An in-memory ref does
// not survive either, so the sidebar fell back to the alphabetically first
// product — connect a source on product B, come back on product A.
//
// Keyed by account because one login can sit in many tenants: switching
// workspace and back should return to each one's own product, and a remembered
// slug must never leak across the boundary. Cached in the module so callers can
// read it on every render.
import { getSession } from "../session";

const KEY = "fk_active_product";

/** account id → product slug */
type Memory = Record<string, string>;

let cached: Memory | undefined;

function read(): Memory {
  if (cached) return cached;
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(KEY) ?? "null");
    cached = parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as Memory) : {};
  } catch {
    cached = {};
  }
  return cached;
}

/** The product this workspace was last on, or null. Callers still resolve it
 *  against the portfolio — a remembered slug can have been deleted. */
export function rememberedProduct(): string | null {
  const accountId = getSession()?.account.id;
  if (!accountId) return null;
  const slug = read()[accountId];
  return typeof slug === "string" && slug ? slug : null;
}

export function rememberProduct(slug: string): void {
  const accountId = getSession()?.account.id;
  if (!accountId) return;
  const mem = read();
  if (mem[accountId] === slug) return;
  mem[accountId] = slug;
  try {
    localStorage.setItem(KEY, JSON.stringify(mem));
  } catch {
    /* private mode: the in-memory copy still covers this tab */
  }
}
